import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import ViewController from "../views/ViewController.vue";
import Register from "../views/Register.vue";
import SignIn from "../views/Signin.vue";
import EnterPin from "../views/EnterPin.vue";
import Error from "../views/Error.vue";
import Scanner from "../views/Scanner.vue";
import DeviceDetection from "../views/DeviceDetection.vue";
import Permissions from "../views/Permissions.vue";

import Wallet from "../views/Wallet.vue";
import Map from "../views/Map.vue";
import AR from "../views/AR.vue";
import Game from "../views/Game.vue";
import { nextTick } from "vue";
import CampaignPaused from "@/views/CampaignPaused.vue";
import { gtmService } from "@/utils/gtm";
import Prize from "@/views/Prize.vue";

import { authStore } from "@/store/pinia_modules/authStore";
import { derivePageObservability, OBSERVABILITY_EVENT_NAMES } from "@/utils/observability";

const routes: Array<RouteRecordRaw> = [
	{
		path: "/error",
		name: "Error",
		component: Error,
	},
	{
		path: "/:campaign",
		name: "Register",
		component: Register,
	},
	{
		path: "/:campaign/sign-in",
		name: "SignIn",
		component: SignIn,
	},
	{
		path: "/:campaign/not-for-desktop",
		name: "DeviceDetection",
		component: DeviceDetection,
	},
	{
		path: "/:campaign/campaign-paused",
		name: "CampaignPaused",
		component: CampaignPaused,
	},
	{
		path: "/:campaign/out-of-plays",
		name: "OutOfPlays",
		component: () => import("@/views/OutOfPlays.vue"),
	},
	{
		path: "/:campaign/enter-pin",
		name: "Enter Pin",
		component: EnterPin,
	},
	{
		path: "/:campaign/vault",
		name: "Wallet",
		component: Wallet,
	},
	{
		path: "/:campaign/map",
		name: "Map",
		component: Map,
	},
	{
		path: "/:campaign/ar-viewer",
		name: "AR",
		component: AR,
	},
	{
		path: "/:campaign/game",
		name: "Game",
		component: Game,
	},
	{
		path: "/:campaign/scanner",
		name: "Scanner",
		component: Scanner,
	},
	{
		path: "/:campaign/permissions",
		name: "Permissions",
		component: Permissions,
	},
	{
		path: "/:campaign/prize/:vitemID",
		name: "Prize",
		component: Prize,
	},
	{
		path: "/:campaign/:page",
		name: "Custom",
		component: ViewController,
	},
];

// history: createWebHistory(import.meta.env.BASE_URL),

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
});

function isMobile(): boolean {
	const userAgent = navigator.userAgent || "";
	const touchCapableMac =
		/Macintosh/i.test(userAgent) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
	let isMobile =
		/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) ||
		touchCapableMac;
	const isDevMode = process.env.NODE_ENV == "development" ? true : false;
	if (isDevMode) {
		isMobile = true;
	}

	return isMobile;
}

router.beforeEach(async (to) => {
	if (!isMobile() && to.name !== "DeviceDetection") {
		return {
			name: "DeviceDetection",
			params: to.params,
			query: to.query,
		};
	}

	nextTick(() => {
		const title = document.querySelector("#site-title");
		if (title) {
			const text = title.textContent;
			if (text) {
				document.title = text;
			}
		}
	});
});

// GTM page tracking
router.afterEach((to, from) => {
	if (to.fullPath === from.fullPath) {
		return;
	}

	nextTick(() => {
		// Track page view in GTM
		gtmService.pushEvent('page_view', {
			page_title: document.title,
			page_location: window.location.href,
			page_path: to.path,
			campaign_id: to.params.campaign,
			from_page: from.path
		});

		// Track page view in Backend
		const campaignId = to.params.campaign;
		if (campaignId && typeof campaignId === 'string') {
			const store = authStore();
			store.ensureCorrelationIds();
			// use userUUID if available, otherwise try userId, otherwise 'anonymous'
			// identifier is required by the API
			const identifier = store.getUserID || store.getUserUUID || 'anonymous';
			
			// include userId if available for better backend tracking
			const pageObservability = derivePageObservability({
				name: typeof to.name === "string" ? to.name : null,
				path: to.path,
			});
			const eventPayload: any = {
				campaignId: campaignId,
				eventName: OBSERVABILITY_EVENT_NAMES.navigationPageViewed,
				event: 'page-view',
				identifier: identifier,
				pageRoute: to.path,
				pageName: pageObservability.pageName,
				pageType: pageObservability.pageType,
				journeyStage: pageObservability.journeyStage,
				journeyStep: pageObservability.journeyStep,
				sessionId: store.getSessionID || undefined,
				journeyId: store.getJourneyID || undefined,
			};
			
			if (store.getUserID) {
				eventPayload.userId = store.getUserID;
			}

			store.dispatchUserEvent(eventPayload);
		}
	});
});

export default router;
